import React, { useCallback, useContext, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { transparentize } from 'polished'
import { Button, Text, Toggle } from '@pancakeswap-libs/uikit'
import { AlertTriangle } from 'react-feather'
import { useExpertModeManager } from 'state/user/hooks'
import useI18n from 'hooks/useI18n'
import QuestionHelper from '../QuestionHelper'

const StyledExpertModeSetting = styled.div`
  margin-bottom: 16px;
`

const Label = styled.div`
  align-items: center;
  display: flex;
`

const ToggleWrapper = styled.div`
  margin-left: auto;
`

const WarningBox = styled.div`
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  background-color: ${({ theme }) => transparentize(0.9, theme.colors.failure)};
  border: 1px solid ${({ theme }) => transparentize(0.5, theme.colors.failure)};
`

const WarningHeader = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 8px;

  svg {
    margin-right: 8px;
    flex-shrink: 0;
  }
`

const WarningActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;

  & > button {
    flex: 1;
  }
`

const ExpertModeSetting = () => {
  const TranslateString = useI18n()
  const theme = useContext(ThemeContext)
  const [expertMode, toggleExpertMode] = useExpertModeManager()
  const [showConfirmation, setShowConfirmation] = useState(false)

  const handleToggle = useCallback(() => {
    if (expertMode) {
      // turning expert mode off never needs acknowledgement
      toggleExpertMode()
      setShowConfirmation(false)
    } else {
      setShowConfirmation(true)
    }
  }, [expertMode, toggleExpertMode])

  const handleConfirm = useCallback(() => {
    toggleExpertMode()
    setShowConfirmation(false)
  }, [toggleExpertMode])

  const handleCancel = useCallback(() => setShowConfirmation(false), [])

  return (
    <StyledExpertModeSetting>
      <Label>
        <Text style={{ fontWeight: 600 }} mr="8px">
          {TranslateString(9994, 'Expert Mode')}
        </Text>
        <QuestionHelper
          text={TranslateString(
            9995,
            'Bypasses the confirm swap screen and disables the high price-impact safety block. Only for advanced users.'
          )}
        />
        <ToggleWrapper>
          <Toggle
            id="expert-mode-toggle"
            checked={expertMode}
            onChange={handleToggle}
            scale="md"
            aria-label={TranslateString(9996, 'Toggle expert mode')}
          />
        </ToggleWrapper>
      </Label>
      {showConfirmation && (
        <WarningBox role="alertdialog" aria-label="Expert mode warning">
          <WarningHeader>
            <AlertTriangle size={20} color={theme.colors.failure} />
            <Text color="failure" bold>
              {TranslateString(9997, 'Are you sure?')}
            </Text>
          </WarningHeader>
          <Text fontSize="14px" color="text">
            {TranslateString(
              9998,
              'Expert mode turns off the confirm transaction prompt and allows high price-impact trades that often result in bad rates and lost funds. This can result in bad trades, use at your own risk.'
            )}
          </Text>
          <WarningActions>
            <Button variant="tertiary" scale="sm" onClick={handleCancel}>
              {TranslateString(9999, 'Cancel')}
            </Button>
            <Button variant="danger" scale="sm" onClick={handleConfirm} id="confirm-expert-mode">
              {TranslateString(10000, 'Turn On Expert Mode')}
            </Button>
          </WarningActions>
        </WarningBox>
      )}
    </StyledExpertModeSetting>
  )
}

export default ExpertModeSetting
